import { isEmpty } from "lodash/fp";
import { DateTime } from "luxon";

import type {
  ApiTopic,
  ApiTopicEntity,
  ListTopicReportsParams,
  PostTopicFeedbackBody,
  ShareTopicViaEmailBody,
  Topic,
  TopicEntity,
  TopicEntityListParams,
  TopicImpersonationCreateRequest,
  TopicImpersonationDeleteRequest,
  TopicImpersonationView,
  TopicPatchRequest,
  TopicResponse,
} from "@app/analytics/types";
import { get, httpDelete, patch, post } from "@app/common/api";
import type { User } from "@app/common/types";
import {
  isResponseErrorWithStatus,
  sanitize,
  withQueryParamsUrl,
} from "@app/common/utils";

import * as TopicsParser from "./parsers/topics";

export const listTopics = async (
  params: ListTopicReportsParams
): Promise<Topic[]> => {
  const queryParams = {
    start_time: params.dateRange?.startDate.toISO(),
    end_time: params.dateRange?.endDate.toISO(),
    view: params.view === "default" ? null : params.view,
  };

  const response: ApiTopic[] = await get(
    withQueryParamsUrl("/topics", queryParams),
    { withCreds: true }
  );

  return TopicsParser.parseTopics(response);
};

export const getTopic = async (
  id: Topic["id"],
  impersonateUserId?: User["id"]
): Promise<TopicResponse> => {
  try {
    const url = withQueryParamsUrl(`/topics/${id}`, {
      impersonate_user_id: impersonateUserId,
    });
    const response: ApiTopic = await get(url, { withCreds: true });
    return TopicsParser.parseTopic(response);
  } catch (error) {
    if (isResponseErrorWithStatus(error, 403)) {
      return { error: "unauthorized" };
    }
    if (isResponseErrorWithStatus(error, 404)) {
      return { error: "not_found" };
    }
    throw error;
  }
};

export const patchTopic = async (
  id: Topic["id"],
  request: TopicPatchRequest
): Promise<void> => {
  return patch(`/topics/${id}`, request, { withCreds: true });
};

export const listTopicEntities = async (
  id: Topic["id"],
  params?: TopicEntityListParams
): Promise<TopicEntity[]> => {
  const queryParams = {
    entity_kind_csv: isEmpty(params?.entityKinds)
      ? null
      : params?.entityKinds?.join(","),
    limit: params?.limit,
    offset: params?.offset,
    impersonate_user_id: params?.impersonateUserId,
  };

  const response: ApiTopicEntity[] = await get(
    withQueryParamsUrl(`/topics/${id}/entities`, queryParams),
    { withCreds: true }
  );
  return TopicsParser.parseTopicEntities(response);
};

export const getTopicEntity = async (
  topicId: Topic["id"],
  entityId: TopicEntity["entityId"]
): Promise<TopicEntity> => {
  const response: ApiTopicEntity = await get(
    `/topics/${topicId}/entities/${entityId}`,
    { withCreds: true }
  );
  return TopicsParser.parseTopicEntity(response);
};

export const shareViaEmail = async ({
  topicId,
  ...body
}: ShareTopicViaEmailBody): Promise<void> => {
  const postBody = {
    ...body,
    timezone: DateTime.now().zoneName,
  };

  return post(`/topics/${topicId}/share-email`, sanitize(postBody), {
    withCreds: true,
  });
};

export const listImpersonations = async (
  topicId: Topic["id"]
): Promise<TopicImpersonationView[]> => {
  const response = await get(`/topics/${topicId}/impersonation`, {
    withCreds: true,
  });
  return response ?? [];
};

export const createImpersonations = async ({
  topicId,
  entities,
}: TopicImpersonationCreateRequest): Promise<void> => {
  return post(
    `/topics/${topicId}/impersonation`,
    { entity_impersonations: entities },
    { withCreds: true }
  );
};

export const deleteImpersonation = async ({
  topicId,
  ...body
}: TopicImpersonationDeleteRequest): Promise<void> => {
  const url = withQueryParamsUrl(`/topics/${topicId}/impersonation`, {
    entity_id: body.entityId,
    entity_kind: body.entityKind,
    ...(body.ownerUserId && { owner_user_id: body.ownerUserId }),
  });

  return httpDelete(url, { withCreds: true });
};

export const postTopicFeedback = async (
  topicId: Topic["id"],
  feedback: PostTopicFeedbackBody
) => {
  return post(`/topics/${topicId}/feedback`, feedback, { withCreds: true });
};
